import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchChallenges,
  fetchChallengeByName,
  takeChallengePhone,
  updateChallengePhone,
  autoAssignChallengePhones,
  subscribeToChallenge,
  subscribeToChallenges
} from '../services/firestoreService';
import { initiateXliteCall } from '../utils/xliteHelper';
import './ChallengesScreen.css';

const ChallengesScreen = () => {
  const { employeeData } = useAuth();
  const [challenges, setChallenges] = useState([]);
  const [loadingChallenges, setLoadingChallenges] = useState(true);
  const [selectedChallengeId, setSelectedChallengeId] = useState(null);
  const [challengeDetails, setChallengeDetails] = useState(null);
  const [loadingChallengeDetails, setLoadingChallengeDetails] = useState(false);
  const [selectedPhoneForTake, setSelectedPhoneForTake] = useState(null);
  const [phoneFormData, setPhoneFormData] = useState({
    clientName: '',
    clientEmail: '',
    clientAddress: '',
    remark: '',
    status: 'New'
  });
  const challengeDetailsRef = useRef(null);
  const challengeUnsubscribeRef = useRef(null);
  const challengesListUnsubscribeRef = useRef(null);
  const autoAssignedRef = useRef(new Set()); // Track which challenges have been auto-assigned

  const employeeName = employeeData?.name || employeeData?.employeeName || 'Unknown';
  const employeeId = employeeData?._id || employeeData?.email || '';

  // Helper function to check if a date is today
  const isToday = (dateStr, timestamp) => {
    if (!dateStr && !timestamp) return false;
    
    let date;
    if (timestamp && timestamp.toDate) {
      date = timestamp.toDate();
    } else if (dateStr) {
      // Parse DD-MM-YYYY format
      const [day, month, year] = dateStr.split('-');
      date = new Date(parseInt(`20${year}`), parseInt(month) - 1, parseInt(day));
    } else {
      return false;
    }

    const today = new Date();
    const dateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    
    return dateOnly.getTime() === todayOnly.getTime();
  };

  // Helper function to filter phone numbers based on limitperDay
  const filterPhonesByLimit = (phoneDataArray, settings, employeeName) => {
    if (!phoneDataArray || !Array.isArray(phoneDataArray)) return [];
    
    const limitperDay = settings?.limitperDay || 0;
    
    // If limitperDay is 0 or not set, show all phones (backward compatibility)
    if (limitperDay === 0) {
      return phoneDataArray;
    }

    // Separate phones into categories
    const takenByMeToday = phoneDataArray.filter(phoneData => 
      phoneData.isTaken && 
      phoneData.takenBy === employeeName && 
      isToday(phoneData.date, phoneData.takenAt)
    );
    
    const availablePhones = phoneDataArray.filter(phoneData => !phoneData.isTaken);
    const takenByOthers = phoneDataArray.filter(phoneData => 
      phoneData.isTaken && 
      (phoneData.takenBy !== employeeName || !isToday(phoneData.date, phoneData.takenAt))
    );

    // Count phones already taken by this employee today
    const takenTodayCount = takenByMeToday.length;

    // Calculate remaining limit
    const remainingLimit = limitperDay - takenTodayCount;
    
    // If already reached the limit, only show phones already taken by this employee today + taken by others
    if (remainingLimit <= 0) {
      return [...takenByMeToday, ...takenByOthers];
    }

    // Limit available phones to remainingLimit
    const limitedAvailable = availablePhones.slice(0, remainingLimit);
    
    // Return in order: taken by me today, limited available phones, taken by others
    return [...takenByMeToday, ...limitedAvailable, ...takenByOthers];
  };

  useEffect(() => {
    // Initial fetch
    fetchChallengesList();
    
    // Set up real-time subscription for challenges list
    const unsubscribe = subscribeToChallenges((challengesData) => {
      // Filter challenges based on asignTo array
      const filteredChallenges = (Array.isArray(challengesData) ? challengesData : []).filter(challenge => {
        const asignTo = challenge.settings?.asignTo || [];
        // If asignTo is empty, show to all employees (backward compatibility)
        // Otherwise, only show if employee name is in the array
        return asignTo.length === 0 || asignTo.includes(employeeName);
      });
      setChallenges(filteredChallenges);
      setLoadingChallenges(false);
    });
    
    challengesListUnsubscribeRef.current = unsubscribe;
    
    // Cleanup on unmount
    return () => {
      if (challengesListUnsubscribeRef.current) {
        challengesListUnsubscribeRef.current();
        challengesListUnsubscribeRef.current = null;
      }
    };
  }, [employeeName]);

  // Auto-scroll to challenge details table when it opens
  useEffect(() => {
    if (challengeDetails && challengeDetailsRef.current) {
      setTimeout(() => {
        challengeDetailsRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [challengeDetails]);


  // Real-time subscription to challenge details
  useEffect(() => {
    // Clean up previous subscription
    if (challengeUnsubscribeRef.current) {
      challengeUnsubscribeRef.current();
      challengeUnsubscribeRef.current = null;
    }

    if (selectedChallengeId) {
      // Set up real-time listener for challenge updates
      const unsubscribe = subscribeToChallenge(selectedChallengeId, (challengeData) => {
        if (challengeData) {
          // Transform phone data into array for display
          const phoneNumbers = Object.keys(challengeData.challengeData || {}).filter(key => 
            key !== 'settings' && challengeData.challengeData[key] && typeof challengeData.challengeData[key] === 'object' && challengeData.challengeData[key].date
          );
          
          let phoneDataArray = phoneNumbers.map(phone => ({
            phoneNumber: phone,
            ...challengeData.challengeData[phone]
          }));
          
          // Filter phone numbers based on limitperDay
          const filteredArray = filterPhonesByLimit(phoneDataArray, challengeData.settings, employeeName);
          
          // Auto-assign available phones that are shown to the employee (only once per day)
          const limitperDay = challengeData.settings?.limitperDay || 0;
          if (limitperDay > 0) {
            const challengeKey = `${challengeData.name}-${new Date().toDateString()}`;
            
            // Only auto-assign if we haven't already done it for this challenge today
            if (!autoAssignedRef.current.has(challengeKey)) {
              // Count phones already taken by this employee today
              const takenToday = phoneDataArray.filter(phoneData => {
                return phoneData.isTaken && 
                       phoneData.takenBy === employeeName && 
                       isToday(phoneData.date, phoneData.takenAt);
              }).length;

              // Calculate remaining limit
              const remainingLimit = limitperDay - takenToday;
              
              if (remainingLimit > 0) {
                // Get available phones from filtered array that are not yet taken
                const availableToAssign = filteredArray
                  .filter(phoneData => !phoneData.isTaken)
                  .slice(0, remainingLimit)
                  .map(phoneData => phoneData.phoneNumber);
                
                // Auto-assign these phones (fire and forget - real-time will update)
                if (availableToAssign.length > 0) {
                  autoAssignedRef.current.add(challengeKey);
                  autoAssignChallengePhones(
                    challengeData.name,
                    availableToAssign,
                    employeeName,
                    employeeId
                  ).catch(error => {
                    console.error('Error auto-assigning phones:', error);
                    autoAssignedRef.current.delete(challengeKey); // Retry on next load if failed
                  });
                }
              }
            }
          }
          
          setChallengeDetails({
            id: challengeData.id,
            name: challengeData.name,
            phoneNumbers: phoneNumbers,
            challengeData: challengeData.challengeData,
            phoneDataArray: filteredArray,
            settings: challengeData.settings
          });
        }
      });

      challengeUnsubscribeRef.current = unsubscribe;
    }

    // Cleanup on unmount or when challenge changes
    return () => {
      if (challengeUnsubscribeRef.current) {
        challengeUnsubscribeRef.current();
        challengeUnsubscribeRef.current = null;
      }
    };
  }, [selectedChallengeId]);

  const fetchChallengesList = async () => {
    try {
      setLoadingChallenges(true);
      const challengesData = await fetchChallenges();
      // Filter challenges based on asignTo array
      const filteredChallenges = (Array.isArray(challengesData) ? challengesData : []).filter(challenge => {
        const asignTo = challenge.settings?.asignTo || [];
        // If asignTo is empty, show to all employees (backward compatibility)
        // Otherwise, only show if employee name is in the array
        return asignTo.length === 0 || asignTo.includes(employeeName);
      });
      setChallenges(filteredChallenges);
    } catch (error) {
      console.error('Error fetching challenges:', error);
      setChallenges([]);
    } finally {
      setLoadingChallenges(false);
    }
  };

  const handleViewChallenge = async (challengeId) => {
    // Toggle: if same challenge is clicked, close it
    if (selectedChallengeId === challengeId) {
      setSelectedChallengeId(null);
      setChallengeDetails(null);
      // Clean up subscription
      if (challengeUnsubscribeRef.current) {
        challengeUnsubscribeRef.current();
        challengeUnsubscribeRef.current = null;
      }
      return;
    }

    setSelectedChallengeId(challengeId);
    setLoadingChallengeDetails(true);
    
    try {
      // Initial fetch - real-time subscription will take over after this
      const challengeData = await fetchChallengeByName(challengeId);
      if (challengeData) {
        // Transform phone data into array for display
        let phoneDataArray = challengeData.phoneNumbers.map(phone => ({
          phoneNumber: phone,
          ...challengeData.challengeData[phone]
        }));
        
        // Filter phone numbers based on limitperDay
        const filteredArray = filterPhonesByLimit(phoneDataArray, challengeData.settings, employeeName);
        
        // Auto-assign available phones that are shown to the employee (only once per day)
        const limitperDay = challengeData.settings?.limitperDay || 0;
        if (limitperDay > 0) {
          const challengeKey = `${challengeData.name}-${new Date().toDateString()}`;
          
          // Only auto-assign if we haven't already done it for this challenge today
          if (!autoAssignedRef.current.has(challengeKey)) {
            // Count phones already taken by this employee today
            const takenToday = phoneDataArray.filter(phoneData => {
              return phoneData.isTaken && 
                     phoneData.takenBy === employeeName && 
                     isToday(phoneData.date, phoneData.takenAt);
            }).length;

            // Calculate remaining limit
            const remainingLimit = limitperDay - takenToday;
            
            if (remainingLimit > 0) {
              // Get available phones from filtered array that are not yet taken
              const availableToAssign = filteredArray
                .filter(phoneData => !phoneData.isTaken)
                .slice(0, remainingLimit)
                .map(phoneData => phoneData.phoneNumber);
              
              // Auto-assign these phones
              if (availableToAssign.length > 0) {
                try {
                  autoAssignedRef.current.add(challengeKey);
                  await autoAssignChallengePhones(
                    challengeData.name,
                    availableToAssign,
                    employeeName,
                    employeeId
                  );
                  // Refresh challenge data after assignment
                  const updatedChallengeData = await fetchChallengeByName(challengeId);
                  if (updatedChallengeData) {
                    let updatedPhoneDataArray = updatedChallengeData.phoneNumbers.map(phone => ({
                      phoneNumber: phone,
                      ...updatedChallengeData.challengeData[phone]
                    }));
                    updatedPhoneDataArray = filterPhonesByLimit(updatedPhoneDataArray, updatedChallengeData.settings, employeeName);
                    phoneDataArray = updatedPhoneDataArray;
                  }
                } catch (error) {
                  console.error('Error auto-assigning phones:', error);
                  autoAssignedRef.current.delete(challengeKey); // Retry on next load if failed
                  // Continue even if auto-assignment fails
                }
              }
            }
          }
        }
        
        setChallengeDetails({
          ...challengeData,
          phoneDataArray: filteredArray.length > 0 ? filteredArray : phoneDataArray
        });
      }
    } catch (error) {
      console.error('Error fetching challenge details:', error);
      setChallengeDetails(null);
    } finally {
      setLoadingChallengeDetails(false);
    }
  };

  const handleTakePhoneClick = (phoneNumber, phoneData) => {
    setSelectedPhoneForTake(phoneNumber);
    // Pre-fill form data with existing data if phone is already taken by this employee
    if (phoneData && phoneData.isTaken && phoneData.takenBy === employeeName) {
      setPhoneFormData({
        clientName: phoneData.clientName || '',
        clientEmail: phoneData.clientEmail || '',
        clientAddress: phoneData.clientAddress || '',
        remark: phoneData.remark || '',
        status: phoneData.status || 'New'
      });
    } else {
      // Reset form data for new assignment
      setPhoneFormData({
        clientName: '',
        clientEmail: '',
        clientAddress: '',
        remark: '',
        status: 'New'
      });
    }
  };

  const handleTakePhoneSubmit = async () => {
    if (!selectedPhoneForTake || !challengeDetails) {
      return;
    }

    try {
      // Get the phone data to check if it's already taken
      const phoneData = challengeDetails.phoneDataArray.find(p => p.phoneNumber === selectedPhoneForTake);
      const isAlreadyTaken = phoneData && phoneData.isTaken && phoneData.takenBy === employeeName;

      // If not already taken, check limitperDay before taking the phone
      if (!isAlreadyTaken) {
        const limitperDay = challengeDetails.settings?.limitperDay || 0;
        
        if (limitperDay > 0) {
          // Count phones already taken by this employee today
          const takenToday = challengeDetails.phoneDataArray.filter(phoneData => {
            return phoneData.isTaken && 
                   phoneData.takenBy === employeeName && 
                   isToday(phoneData.date, phoneData.takenAt);
          }).length;

          // Check if limit is reached
          if (takenToday >= limitperDay) {
            alert(`You have reached your daily limit of ${limitperDay} phone(s) for this challenge. Please try again tomorrow.`);
            return;
          }
        }

        // First take the phone if not already taken
        await takeChallengePhone(challengeDetails.name, selectedPhoneForTake, employeeName, employeeId);
      }
      
      // Update all the details (works for both new assignment and update)
      await updateChallengePhone(challengeDetails.name, selectedPhoneForTake, {
        clientName: phoneFormData.clientName.trim(),
        clientEmail: phoneFormData.clientEmail.trim(),
        clientAddress: phoneFormData.clientAddress.trim(),
        remark: phoneFormData.remark.trim(),
        status: phoneFormData.status
      });

      alert(isAlreadyTaken ? 'Details updated successfully!' : 'Phone taken and details updated successfully!');
      
      // Close the form
      setSelectedPhoneForTake(null);
      setPhoneFormData({
        clientName: '',
        clientEmail: '',
        clientAddress: '',
        remark: '',
        status: 'New'
      });
      
      // Real-time subscription will automatically update the UI
      await fetchChallengesList();
    } catch (error) {
      console.error('Error updating phone:', error);
      alert(error.message || 'Error updating phone. Please try again.');
    }
  };

  const parseDate = (dateStr) => {
    if (!dateStr) {
      const now = new Date();
      const day = String(now.getDate()).padStart(2, '0');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
        'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[now.getMonth()];
      const year = now.getFullYear();
      return { day, month, year };
    }
    const [day, month, year] = dateStr.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
      'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
    const monthName = monthNames[parseInt(month) - 1] || '';
    return { day, month: monthName, year: `20${year}` };
  };



  return (
    <div className="challenges-screen">
      <div className="challenges-container">
        <div className="challenges-header">
          <h1>Challenges</h1>
          <p>View and participate in team challenges</p>
        </div>

        {loadingChallenges ? (
          <div className="loading-state">
            <p>Loading challenges...</p>
          </div>
        ) : challenges.length === 0 ? (
          <div className="empty-state">
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
            <h3>No Challenges Available</h3>
            <p>There are no challenges available at the moment. Check back later!</p>
          </div>
        ) : (
          <div className="challenges-list">
            {challenges.map((challenge, index) => {
              const dateInfo = parseDate(challenge.createdAt);
              const isEven = index % 2 === 0;
              const isLast = index === challenges.length - 1;
              
              return (
                <div key={challenge.id} className={`challenge-card ${isEven ? 'left' : 'right'}`}>
                  <div className="challenge-card-content">
                    <div className="challenge-header-info">
                      <h3>{challenge.name}</h3>
                      {challenge.isPremium && (
                        <span className="premium-badge">Premium</span>
                      )}
                    </div>
                    <p className="challenge-description">
                      {challenge.isPremium ? 'Premium Challenge' : 'Standard Challenge'} • {challenge.totalPhones} entries
                    </p>
                    <div className="challenge-stats">
                      <div className="stat-item">
                        <span className="stat-number">{challenge.totalPhones}</span>
                        <span className="stat-label">Total</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-number">{challenge.takenPhones}</span>
                        <span className="stat-label">Taken</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-number">{challenge.availablePhones}</span>
                        <span className="stat-label">Available</span>
                      </div>
                    </div>
                    <button
                      className="view-challenge-btn"
                      onClick={() => handleViewChallenge(challenge.id)}
                    >
                      {selectedChallengeId === challenge.id ? 'Hide Details' : 'View Details'}
                    </button>
                    <div className="challenge-date">
                      <span>{dateInfo.month} {dateInfo.day}, {dateInfo.year}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Challenge Details Table */}
        {selectedChallengeId && challengeDetails && (
          <div className="challenge-details-section" ref={challengeDetailsRef}>
            <div className="challenge-details-header">
              <h2>{challengeDetails.name}</h2>
              <button
                className="close-details-btn"
                onClick={() => {
                  setSelectedChallengeId(null);
                  setChallengeDetails(null);
                  setSelectedPhoneForTake(null);
                  setPhoneFormData({
                    clientName: '',
                    clientEmail: '',
                    clientAddress: '',
                    remark: '',
                    status: 'New'
                  });
                }}
              >
                ×
              </button>
            </div>
            {loadingChallengeDetails ? (
              <div className="loading-state">
                <p>Loading challenge details...</p>
              </div>
            ) : challengeDetails.phoneDataArray && challengeDetails.phoneDataArray.length > 0 ? (
              <div className="challenge-table-wrapper">
                {/* Take Phone Form Modal */}
                {selectedPhoneForTake && (
                  <div className="note-input-modal">
                    <div className="phone-form-modal-content">
                      <div className="note-input-modal-header">
                        <div className="header-content">
                          <h3>Call: {selectedPhoneForTake}</h3>
                          <div className="form-call-note">
                            <p>ℹ️ <strong>Note:</strong> After clicking the "Call" button, please remember to end the call from your X-Lite dialer when finished.</p>
                          </div>
                    </div>
                        <button
                          className="note-input-modal-close"
                          onClick={() => {
                            setSelectedPhoneForTake(null);
                            setPhoneFormData({
                              clientName: '',
                              clientEmail: '',
                              clientAddress: '',
                              remark: '',
                              status: 'New'
                            });
                          }}
                        >
                          ×
                        </button>
                        </div>
                      <div className="phone-form-modal-body">
                        <div className="form-group">
                          <label>Client Name</label>
                        <input
                          type="text"
                            className="form-input"
                            placeholder="Enter client name"
                            value={phoneFormData.clientName}
                            onChange={(e) => setPhoneFormData({ ...phoneFormData, clientName: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Client Email</label>
                          <input
                            type="email"
                            className="form-input"
                            placeholder="Enter client email"
                            value={phoneFormData.clientEmail}
                            onChange={(e) => setPhoneFormData({ ...phoneFormData, clientEmail: e.target.value })}
                          />
                        </div>
                        <div className="form-group">
                          <label>Client Address</label>
                          <textarea
                            className="form-textarea"
                            placeholder="Enter client address"
                            value={phoneFormData.clientAddress}
                            onChange={(e) => setPhoneFormData({ ...phoneFormData, clientAddress: e.target.value })}
                            rows="3"
                          />
                        </div>
                        <div className="form-group">
                          <label>Status</label>
                          <select
                            className="form-select"
                            value={phoneFormData.status}
                            onChange={(e) => setPhoneFormData({ ...phoneFormData, status: e.target.value })}
                          >
                            <option value="New">New</option>
                            <option value="Contacted">Contacted</option>
                            <option value="Interested">Interested</option>
                            <option value="Not Interested">Not Interested</option>
                            <option value="Follow Up">Follow Up</option>
                            <option value="Converted">Converted</option>
                          </select>
                        </div>
                        <div className="form-group">
                          <label>Remark/Note</label>
                          <textarea
                            className="form-textarea"
                            placeholder="Enter remark or note"
                            value={phoneFormData.remark}
                            onChange={(e) => setPhoneFormData({ ...phoneFormData, remark: e.target.value })}
                            rows="3"
                          />
                        </div>
                      </div>
                      <div className="note-input-modal-footer">
                          <button
                          className="note-input-cancel-btn"
                            onClick={() => {
                            setSelectedPhoneForTake(null);
                            setPhoneFormData({
                              clientName: '',
                              clientEmail: '',
                              clientAddress: '',
                              remark: '',
                              status: 'New'
                            });
                            }}
                          >
                            Cancel
                          </button>
                        <button
                          className="note-input-call-btn"
                          onClick={async () => {
                            if (selectedPhoneForTake) {
                              // Get employee extension from employeeData
                              const extension = employeeData?.extensionNumber || employeeData?.extension || employeeData?.exten || employeeData?.ext || '7001';
                              await initiateXliteCall(selectedPhoneForTake, extension);
                            }
                          }}
                          title={`Call ${selectedPhoneForTake} using X-Lite`}
                        >
                          📞 Call
                        </button>
                        <button
                          className="note-input-submit-btn"
                          onClick={handleTakePhoneSubmit}
                        >
                          {selectedPhoneForTake && challengeDetails?.phoneDataArray?.find(p => p.phoneNumber === selectedPhoneForTake && p.isTaken && p.takenBy === employeeName) ? 'Update' : 'Save'}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                <div className="challenge-table-container">
                  <table className="challenge-table">
                    <thead>
                      <tr>
                        <th>Phone Number</th>
                        <th>Date</th>
                        <th>Status</th>
                        <th>Taken By</th>
                        <th>Remark/Note</th>
                        <th>Client Name</th>
                        <th>Client Email</th>
                        <th>Client Address</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {challengeDetails.phoneDataArray.map((phoneData) => (
                        <tr key={phoneData.phoneNumber} className={phoneData.isTaken ? 'taken-row' : 'available-row'}>
                          <td>{phoneData.phoneNumber}</td>
                          <td>{phoneData.date || 'N/A'}</td>
                          <td>
                            <span className={`status-badge status-${phoneData.status?.toLowerCase() || 'new'}`}>
                              {phoneData.status || 'New'}
                          </span>
                          </td>
                          <td>{phoneData.takenBy || '-'}</td>
                          <td>{phoneData.remark || '-'}</td>
                          <td>{phoneData.clientName || '-'}</td>
                          <td>{phoneData.clientEmail || '-'}</td>
                          <td>{phoneData.clientAddress || '-'}</td>
                          <td>
                            {!phoneData.isTaken ? (
                          <button
                                className="table-take-btn"
                                onClick={() => handleTakePhoneClick(phoneData.phoneNumber, phoneData)}
                          >
                                Call
                          </button>
                            ) : phoneData.takenBy === employeeName ? (
                          <button
                                className="table-take-btn"
                                onClick={() => handleTakePhoneClick(phoneData.phoneNumber, phoneData)}
                          >
                                Call
                          </button>
                            ) : (
                              <span className="table-taken-indicator">Taken</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                          </div>
                        </div>
            ) : (
              <div className="empty-state">
                <p>No phone numbers available in this challenge</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ChallengesScreen;
